import { useState, useEffect, useCallback, useRef } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ChefHat, Clock, Check, Play, UtensilsCrossed, 
  RefreshCw, Bell, Timer, Store, Volume2, VolumeX,
  Lock, LogOut, Key, Printer, Settings
} from 'lucide-react';
import { toast } from 'sonner';

// Order status flow: pending -> preparing -> ready -> served
const ORDER_STATUSES = {
  pending: { label: 'New', color: 'bg-red-500', icon: Bell, next: 'preparing' },
  preparing: { label: 'Preparing', color: 'bg-amber-500', icon: ChefHat, next: 'ready' },
  ready: { label: 'Ready', color: 'bg-green-500', icon: Check, next: 'served' },
  served: { label: 'Served', color: 'bg-blue-500', icon: UtensilsCrossed, next: null }
};

// Sound presets
const SOUND_PRESETS = {
  beep: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH6LkZWTj4aBc2ptdH+JkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4iRlZOQjIV8cGltdH+IkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4g=',
  chime: 'data:audio/wav;base64,UklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19teleUklGRl9vT19tele='
};

export default function KitchenDisplayPopup({ user, onLogout }) {
  // Auth state
  const [isLocked, setIsLocked] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockCredential, setUnlockCredential] = useState('');
  const [unlockMethod, setUnlockMethod] = useState('pin');
  
  // Restaurant selection
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  
  // Orders
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // KDS Settings
  const [kdsSettings, setKdsSettings] = useState({
    sound_enabled: true,
    sound_type: 'beep',
    sound_volume: 50,
    sound_on_new_order: true,
    sound_on_preparing: false,
    sound_on_ready: true,
    custom_sound_url: '',
    print_enabled: false,
    auto_print_trigger: 'manual_only',
    printer_type: 'browser',
    show_order_number: true,
    show_timestamp: true,
    show_table_info: true,
    show_server_name: false,
    show_order_type: true,
    show_special_notes: true,
    ticket_header_text: '',
    ticket_footer_text: ''
  });
  
  // Sound state
  const [soundMuted, setSoundMuted] = useState(false);
  const previousOrdersRef = useRef({});
  const lastOrderCountRef = useRef({ pending: 0, preparing: 0, ready: 0 });

  // Load restaurants
  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const res = await api.get('/restaurants');
        const activeRestaurants = res.data.filter(r => r.is_active !== false);
        setRestaurants(activeRestaurants);
        
        if (activeRestaurants.length === 1) {
          setSelectedRestaurant(activeRestaurants[0]);
        }
      } catch (error) {
        console.error('Failed to load restaurants');
      } finally {
        setLoading(false);
      }
    };
    loadRestaurants();
  }, []);

  // Load KDS settings after unlock
  useEffect(() => {
    if (!isLocked) {
      loadKdsSettings();
    }
  }, [isLocked]);

  const loadKdsSettings = async () => {
    try {
      const res = await api.get('/kds/settings');
      setKdsSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to load KDS settings');
    }
  };

  // Handle unlock
  const handleUnlock = async () => {
    if (!unlockCredential) {
      toast.error('Please enter your PIN or password');
      return;
    }
    setUnlocking(true);
    try {
      await api.post('/pos/unlock', { method: unlockMethod, credential: unlockCredential });
      setIsLocked(false);
      setUnlockCredential('');
      toast.success('KDS unlocked');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setUnlocking(false);
    }
  };

  // Play sound based on settings
  const playSound = useCallback((eventType) => {
    if (soundMuted || !kdsSettings.sound_enabled) return;
    
    // Check if this event should trigger sound
    if (eventType === 'new_order' && !kdsSettings.sound_on_new_order) return;
    if (eventType === 'preparing' && !kdsSettings.sound_on_preparing) return;
    if (eventType === 'ready' && !kdsSettings.sound_on_ready) return;

    try {
      if (kdsSettings.sound_type === 'tts') {
        const messages = {
          new_order: 'New order received',
          preparing: 'Order is being prepared',
          ready: 'Order is ready'
        };
        const utterance = new SpeechSynthesisUtterance(messages[eventType] || 'Alert');
        utterance.volume = kdsSettings.sound_volume / 100;
        speechSynthesis.speak(utterance);
      } else {
        let soundUrl;
        if (kdsSettings.sound_type === 'custom' && kdsSettings.custom_sound_url) {
          soundUrl = kdsSettings.custom_sound_url;
        } else {
          soundUrl = SOUND_PRESETS[kdsSettings.sound_type] || SOUND_PRESETS.beep;
        }
        
        const audio = new Audio(soundUrl);
        audio.volume = kdsSettings.sound_volume / 100;
        audio.play().catch(() => {});
      }
    } catch (e) {
      console.error('Sound playback failed');
    }
  }, [soundMuted, kdsSettings]);

  // Print kitchen ticket
  const printTicket = useCallback((order) => {
    if (!kdsSettings.print_enabled) return;

    const ticketContent = generateTicketHTML(order);
    
    if (kdsSettings.printer_type === 'browser') {
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      printWindow.document.write(ticketContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 500);
    } else {
      // For thermal printer - would need backend proxy
      toast.info('Thermal printing requires additional setup');
    }
  }, [kdsSettings]);

  // Generate ticket HTML
  const generateTicketHTML = (order) => {
    const s = kdsSettings;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0; padding: 10px; }
          .header { text-align: center; font-weight: bold; font-size: 14px; border-bottom: 1px dashed #000; padding-bottom: 5px; margin-bottom: 5px; }
          .footer { text-align: center; border-top: 1px dashed #000; padding-top: 5px; margin-top: 5px; }
          .item { margin: 3px 0; }
          .qty { display: inline-block; width: 30px; font-weight: bold; }
          .notes { font-size: 10px; background: #f0f0f0; padding: 5px; margin-top: 5px; }
          .divider { border-top: 1px dashed #000; margin: 5px 0; }
        </style>
      </head>
      <body>
        ${s.ticket_header_text ? `<div class="header">${s.ticket_header_text}</div>` : ''}
        ${s.show_order_number ? `<div>Order #: ${order.id?.slice(-6).toUpperCase() || 'N/A'}</div>` : ''}
        ${s.show_timestamp ? `<div>Time: ${new Date(order.created_at).toLocaleTimeString()}</div>` : ''}
        ${s.show_table_info && order.table_number ? `<div>Table: ${order.table_number}</div>` : ''}
        ${s.show_table_info && order.room_number ? `<div>Room: ${order.room_number}</div>` : ''}
        ${s.show_order_type ? `<div>Type: ${order.order_type?.replace('_', ' ') || 'N/A'}</div>` : ''}
        ${s.show_server_name && order.server_name ? `<div>Server: ${order.server_name}</div>` : ''}
        <div class="divider"></div>
        ${order.items?.map(item => `
          <div class="item"><span class="qty">${item.quantity}x</span> ${item.item_name}</div>
        `).join('') || ''}
        ${s.show_special_notes && order.notes ? `<div class="notes"><strong>Note:</strong> ${order.notes}</div>` : ''}
        ${s.ticket_footer_text ? `<div class="footer">${s.ticket_footer_text}</div>` : ''}
      </body>
      </html>
    `;
  };

  // Load orders with sound and auto-print handling
  const loadOrders = useCallback(async () => {
    if (!selectedRestaurant) return;
    
    try {
      const res = await api.get(`/orders?restaurant_id=${selectedRestaurant.id}`);
      
      const statuses = ['pending', 'preparing', 'ready'];
      const kitchenOrders = res.data.filter(order => statuses.includes(order.status));
      
      const statusOrder = { pending: 0, preparing: 1, ready: 2 };
      kitchenOrders.sort((a, b) => {
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;
        return new Date(a.created_at) - new Date(b.created_at);
      });

      // Check for status changes and play sounds
      const currentCounts = {
        pending: kitchenOrders.filter(o => o.status === 'pending').length,
        preparing: kitchenOrders.filter(o => o.status === 'preparing').length,
        ready: kitchenOrders.filter(o => o.status === 'ready').length
      };

      // Detect new orders
      if (currentCounts.pending > lastOrderCountRef.current.pending) {
        playSound('new_order');
        
        // Auto-print new orders if configured
        if (kdsSettings.print_enabled && kdsSettings.auto_print_trigger === 'new_order') {
          const newOrders = kitchenOrders.filter(o => 
            o.status === 'pending' && !previousOrdersRef.current[o.id]
          );
          newOrders.forEach(order => printTicket(order));
        }
      }

      // Detect orders started preparing
      if (currentCounts.preparing > lastOrderCountRef.current.preparing) {
        playSound('preparing');
        
        if (kdsSettings.print_enabled && kdsSettings.auto_print_trigger === 'preparing') {
          const newPreparingOrders = kitchenOrders.filter(o => 
            o.status === 'preparing' && previousOrdersRef.current[o.id]?.status === 'pending'
          );
          newPreparingOrders.forEach(order => printTicket(order));
        }
      }

      // Detect orders ready
      if (currentCounts.ready > lastOrderCountRef.current.ready) {
        playSound('ready');
      }

      // Update refs
      lastOrderCountRef.current = currentCounts;
      previousOrdersRef.current = kitchenOrders.reduce((acc, o) => {
        acc[o.id] = { status: o.status };
        return acc;
      }, {});
      
      setOrders(kitchenOrders);
    } catch (error) {
      console.error('Failed to load orders');
    }
  }, [selectedRestaurant, kdsSettings, playSound, printTicket]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!selectedRestaurant || isLocked) return;
    
    loadOrders();
    
    if (autoRefresh) {
      const interval = setInterval(loadOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [loadOrders, autoRefresh, selectedRestaurant, isLocked]);

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Order marked as ${ORDER_STATUSES[newStatus].label}`);
      loadOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Update individual item status
  const updateItemStatus = async (orderId, itemIndex, newStatus) => {
    try {
      await api.patch(`/orders/${orderId}/items/${itemIndex}/status?status=${newStatus}`);
      toast.success(`Item marked as ${ORDER_STATUSES[newStatus].label}`);
      loadOrders();
    } catch (error) {
      toast.error('Failed to update item status');
    }
  };

  // Get time elapsed since order was created
  const getElapsedTime = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Get urgency color based on time
  const getUrgencyColor = (createdAt, status) => {
    if (status === 'ready') return 'border-green-400';
    
    const created = new Date(createdAt);
    const now = new Date();
    const diffMins = Math.floor((now - created) / 60000);
    
    if (diffMins > 20) return 'border-red-500 animate-pulse';
    if (diffMins > 10) return 'border-amber-500';
    return 'border-gray-200';
  };

  // Group orders by status
  const pendingOrders = orders.filter(o => o.status === 'pending');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');

  // Lock Screen
  if (isLocked) {
    return (
      <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-sm mx-auto p-8">
          <div className="h-24 w-24 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-6 backdrop-blur">
            <ChefHat className="h-12 w-12 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>Kitchen Display</h1>
          <p className="text-white/70 mb-8">Enter your PIN or password to access</p>
          
          <div className="flex justify-center gap-2 mb-6">
            <button
              onClick={() => setUnlockMethod('pin')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${unlockMethod === 'pin' ? 'bg-amber-500 text-white' : 'bg-white/20 text-white'}`}
            >
              <Key className="h-4 w-4 inline mr-1" /> PIN
            </button>
            <button
              onClick={() => setUnlockMethod('password')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${unlockMethod === 'password' ? 'bg-amber-500 text-white' : 'bg-white/20 text-white'}`}
            >
              <Lock className="h-4 w-4 inline mr-1" /> Password
            </button>
          </div>

          <Input
            type={unlockMethod === 'pin' ? 'text' : 'password'}
            placeholder={unlockMethod === 'pin' ? 'Enter PIN' : 'Enter Password'}
            value={unlockCredential}
            onChange={(e) => setUnlockCredential(unlockMethod === 'pin' ? e.target.value.replace(/\D/g, '') : e.target.value)}
            maxLength={unlockMethod === 'pin' ? 6 : 100}
            className="w-full mb-4 text-center bg-white/10 border-white/20 text-white placeholder:text-white/50 text-xl"
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
          />

          <Button
            onClick={handleUnlock}
            disabled={unlocking}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold h-12"
          >
            {unlocking ? 'Unlocking...' : 'Unlock KDS'}
          </Button>

          {onLogout && (
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full mt-4 text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Restaurant Selection Screen
  if (!selectedRestaurant) {
    return (
      <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="max-w-3xl w-full mx-4">
          <div className="text-center mb-8">
            <div className="h-20 w-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4 backdrop-blur">
              <ChefHat className="h-10 w-10 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>Kitchen Display</h1>
            <p className="text-white/70">Select your restaurant kitchen</p>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-10 w-10 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
            </div>
          ) : restaurants.length === 0 ? (
            <div className="text-center text-white/70">
              <p>No restaurants found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {restaurants.map((restaurant) => (
                <Card 
                  key={restaurant.id}
                  className="cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-200 bg-white/10 backdrop-blur border-white/20"
                  onClick={() => setSelectedRestaurant(restaurant)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-xl bg-amber-500 flex items-center justify-center">
                        <Store className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-white">{restaurant.name}</h3>
                        <p className="text-sm text-white/50">{restaurant.cuisine_type || 'Restaurant'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {onLogout && (
            <div className="text-center mt-8">
              <Button
                onClick={onLogout}
                variant="ghost"
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main KDS View
  return (
    <div className="h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-amber-400" />
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              {selectedRestaurant.name}
            </h1>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSelectedRestaurant(null)}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            Change
          </Button>
        </div>

        <div className="flex items-center gap-4">
          {/* Stats */}
          <div className="flex items-center gap-3">
            <Badge className="bg-red-500 text-white px-3 py-1 text-lg">
              <Bell className="h-4 w-4 mr-1" /> {pendingOrders.length}
            </Badge>
            <Badge className="bg-amber-500 text-white px-3 py-1 text-lg">
              <ChefHat className="h-4 w-4 mr-1" /> {preparingOrders.length}
            </Badge>
            <Badge className="bg-green-500 text-white px-3 py-1 text-lg">
              <Check className="h-4 w-4 mr-1" /> {readyOrders.length}
            </Badge>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundMuted(!soundMuted)}
              className={`text-white hover:bg-white/10 ${soundMuted ? 'text-red-400' : ''}`}
              title={soundMuted ? 'Unmute sounds' : 'Mute sounds'}
            >
              {soundMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-white hover:bg-white/10 ${autoRefresh ? 'text-green-400' : ''}`}
              title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
            >
              <RefreshCw className={`h-5 w-5 ${autoRefresh ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadOrders}
              className="border-gray-600 text-white hover:bg-white/10"
            >
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsLocked(true)}
              className="text-white/70 hover:text-white hover:bg-white/10"
              title="Lock KDS"
            >
              <Lock className="h-5 w-5" />
            </Button>
            {onLogout && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="text-white/70 hover:text-white hover:bg-white/10"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="flex-1 grid grid-cols-3 gap-4 p-4 overflow-hidden">
        {/* New Orders Column */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-4 w-4 rounded-full bg-red-500 animate-pulse" />
            <h2 className="font-bold text-white text-lg">NEW ({pendingOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {pendingOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                onItemStatusChange={updateItemStatus}
                onPrint={printTicket}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                printEnabled={kdsSettings.print_enabled}
              />
            ))}
            {pendingOrders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No new orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Preparing Column */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-4 w-4 rounded-full bg-amber-500" />
            <h2 className="font-bold text-white text-lg">PREPARING ({preparingOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {preparingOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                onItemStatusChange={updateItemStatus}
                onPrint={printTicket}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                printEnabled={kdsSettings.print_enabled}
              />
            ))}
            {preparingOrders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <ChefHat className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nothing cooking</p>
              </div>
            )}
          </div>
        </div>

        {/* Ready Column */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-4 w-4 rounded-full bg-green-500" />
            <h2 className="font-bold text-white text-lg">READY ({readyOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {readyOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                onItemStatusChange={updateItemStatus}
                onPrint={printTicket}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                printEnabled={kdsSettings.print_enabled}
              />
            ))}
            {readyOrders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Check className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No orders ready</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Order Card Component
function OrderCard({ order, onStatusChange, onItemStatusChange, onPrint, getElapsedTime, getUrgencyColor, printEnabled }) {
  const statusInfo = ORDER_STATUSES[order.status];
  const StatusIcon = statusInfo?.icon || Bell;
  
  return (
    <Card 
      className={`border-2 ${getUrgencyColor(order.created_at, order.status)} bg-gray-800 transition-all hover:shadow-lg`}
    >
      <CardHeader className="p-3 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${statusInfo?.color} text-white`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusInfo?.label}
            </Badge>
            {order.order_type === 'dine_in' && order.table_number && (
              <Badge variant="outline" className="font-bold text-white border-white/30">
                Table {order.table_number}
              </Badge>
            )}
            {order.order_type === 'room_service' && (
              <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-400/30">
                Room {order.room_number || order.room_id?.slice(-4)}
              </Badge>
            )}
            {order.order_type === 'takeaway' && (
              <Badge variant="outline" className="bg-blue-500/20 text-blue-300 border-blue-400/30">
                Takeaway
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {printEnabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPrint(order)}
                className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-white/10"
                title="Print ticket"
              >
                <Printer className="h-3 w-3" />
              </Button>
            )}
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Timer className="h-3 w-3" />
              {getElapsedTime(order.created_at)}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        {/* Order Items with individual status */}
        <div className="space-y-2 mb-3">
          {order.items?.map((item, idx) => {
            const itemStatus = item.status || order.status || 'pending';
            const itemStatusInfo = ORDER_STATUSES[itemStatus] || ORDER_STATUSES.pending;
            const ItemStatusIcon = itemStatusInfo.icon;
            const nextStatus = itemStatusInfo.next;
            
            return (
              <div key={idx} className="flex items-center gap-2 bg-gray-700/50 rounded p-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded bg-gray-600 text-xs font-bold text-white">
                      {item.quantity}
                    </span>
                    <span className="text-sm text-white truncate">{item.item_name}</span>
                  </div>
                </div>
                
                {/* Item Status Badge + Action */}
                <div className="flex items-center gap-1">
                  <Badge className={`${itemStatusInfo.color} text-white text-xs px-1.5 py-0`}>
                    <ItemStatusIcon className="h-2.5 w-2.5 mr-0.5" />
                    {itemStatusInfo.label}
                  </Badge>
                  
                  {nextStatus && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-6 w-6 p-0 ${
                        itemStatus === 'pending' ? 'text-amber-400 hover:bg-amber-500/20' :
                        itemStatus === 'preparing' ? 'text-green-400 hover:bg-green-500/20' :
                        'text-blue-400 hover:bg-blue-500/20'
                      }`}
                      onClick={() => onItemStatusChange(order.id, idx, nextStatus)}
                      title={`Mark ${nextStatus}`}
                    >
                      {itemStatus === 'pending' && <ChefHat className="h-3.5 w-3.5" />}
                      {itemStatus === 'preparing' && <Check className="h-3.5 w-3.5" />}
                      {itemStatus === 'ready' && <UtensilsCrossed className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Notes */}
        {order.notes && (
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded p-2 text-xs text-yellow-300 mb-3">
            <strong>Note:</strong> {order.notes}
          </div>
        )}
        
        {/* Order Level Action Button */}
        {statusInfo?.next && (
          <Button 
            className={`w-full ${
              order.status === 'pending' ? 'bg-amber-500 hover:bg-amber-600' :
              order.status === 'preparing' ? 'bg-green-500 hover:bg-green-600' :
              'bg-blue-500 hover:bg-blue-600'
            }`}
            onClick={() => onStatusChange(order.id, statusInfo.next)}
          >
            {order.status === 'pending' && (
              <>
                <Play className="h-4 w-4 mr-2" />
                Start All
              </>
            )}
            {order.status === 'preparing' && (
              <>
                <Check className="h-4 w-4 mr-2" />
                All Ready
              </>
            )}
            {order.status === 'ready' && (
              <>
                <UtensilsCrossed className="h-4 w-4 mr-2" />
                Mark Served
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
